import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, GlobalStyles, Tab } from "@mui/material";
import Layout from "components/Layout";
import { useState, useEffect, useRef } from "react";
import StockReport from "components/holdings/Report/StockReport";
import StrategyReport from "components/holdings/Report/StrategyReport";
import { getLastReportTab, updateLastReportTab } from "helpers";
import { ReportTabT } from "helpers/types";

// The reports grow past the window as their filters and figures load:
// reserving the scrollbar's gutter keeps the page from narrowing by its width
// when it appears (task 6149). With the gutter held, a menu or dialog's
// scroll lock no longer needs to pad the page or the fixed header for the
// scrollbar it hides. That padding would narrow the report by the
// scrollbar's width, so its tiles re-wrap and the page jumps while the menu
// is open (task 6535), and it would push the header's contents aside. A
// browser without the gutter keeps the padding, which is then what holds the
// width.
const reportPageGlobalStyles = {
  html: { scrollbarGutter: "stable" },
  "@supports (scrollbar-gutter: stable)": {
    'body[style*="overflow: hidden"], body[style*="overflow: hidden"] .mui-fixed':
      { paddingRight: "0 !important" },
  },
};

export const ReportPage = () => {
  const [tab, setTab] = useState<ReportTabT>(getLastReportTab);
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: ReportTabT) => {
    setTab(newValue);
  };

  useEffect(() => {
    updateLastReportTab(tab);
  }, [tab]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Tab") {
        event.preventDefault();
        setTab((prevTab) => (prevTab === "stock" ? "strategy" : "stock"));
      } else if (
        event.key === "Tab" &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        setTab((prevTab) => (prevTab === "stock" ? "strategy" : "stock"));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Layout title="Report" content="Report page">
      <GlobalStyles styles={reportPageGlobalStyles} />
      <TabContext value={tab}>
        <Box borderBottom={1} borderColor="divider">
          <TabList
            onChange={handleChange}
            ref={tabListRef}
            aria-label="report tabs"
          >
            <Tab label="Stock Report" value="stock" />
            <Tab label="Strategy Report" value="strategy" />
          </TabList>
        </Box>
        <TabPanel value="stock">
          <StockReport />
        </TabPanel>
        <TabPanel value="strategy">
          <StrategyReport />
        </TabPanel>
      </TabContext>
    </Layout>
  );
};

export default ReportPage;
